import {
  ChangeDetectorRef,
  Component,
  Input,
  forwardRef,
  OnInit,
  OnDestroy,
  AfterViewInit,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import {
  Subject,
  Observable,
  of,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  tap,
  catchError
} from 'rxjs';
import { ThemeService } from 'src/app/services/theme.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-icam-autocomplete-select',
  templateUrl: './icam-autocomplete-select.component.html',
  styleUrls: ['./icam-autocomplete-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IcamAutocompleteSelectComponent),
      multi: true
    }
  ]
})
export class IcamAutocompleteSelectComponent
  implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() placeholder = 'Search...';
  @Input() displayField = 'name';
  @Input() valueField = 'id';
  @Input() filterFields: string[] = ['name'];
  @Input() options: any[] = [];
  @Input() fetchFn: ((searchTerm: string) => Observable<any[]>) | null = null;
  @Input() id = '';
  @Input() required = false;
  @Input() translateValue = true;
  @Input() hideClear = false;
  @Input() disabled = false;

  filteredOptions: any[] = [];
  selectedItem: any = null;
  searchControl = new FormControl('');
  isDropdownOpen = false;
  isLoading = false;
  errorMessage = '';
  isLight = true;
  touched = false;
  isValid = false;
  private initializing = true;
  private inputModified = false;

  private destroy$ = new Subject<void>();
  private onChange: (value: any) => void = () => {
    /* noop */
  };
  private onTouched: () => void = () => {
    /* noop */
  };

  constructor(
    private themeService: ThemeService,
    private translateService: TranslateService,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.themeService.isLight$.pipe(takeUntil(this.destroy$)).subscribe((isLight) => (this.isLight = isLight));

    this.searchControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.isLoading = !!this.fetchFn;
          this.errorMessage = '';
        }),
        tap((term) => {
          if (this.selectedItem) {
            const displayValue = this.getTranslatedDisplayValue(this.selectedItem);
            this.inputModified = term !== displayValue;
          } else {
            this.inputModified = !!term;
          }
          this.updateValidity();
          if (!this.isValid) {
            this.onChange(undefined);
          }
        }),
        switchMap((term) => {
          if (typeof term !== 'string') {
            return of(this.options);
          }

          if (this.fetchFn) {
            return this.fetchFn(term).pipe(
              catchError(() => {
                this.errorMessage = 'Failed to load results';
                this.isLoading = false;
                return of([]);
              })
            );
          } else {
            return of(this.filterLocalOptions(term));
          }
        }),
        tap(() => (this.isLoading = false))
      )
      .subscribe((results) => {
        this.filteredOptions = results;

        if (!this.initializing) {
          this.isDropdownOpen = !!this.searchControl.value && this.filteredOptions.length > 0;
        }
        this._cdr.markForCheck();
      });

    setTimeout(() => {
      this.initializing = false;
    }, 500);
  }

  ngAfterViewInit() {
    this.isDropdownOpen = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.setDisabledState(this.disabled);
    }
  }

  onInputFocus() {
    if (this.disabled || this.inputLocked()) return;

    this.markAsTouched();

    if (!this.initializing && this.searchControl.value === '') {
      if (this.fetchFn) {
        this.isLoading = true;
        this.fetchFn('')
          .pipe(
            catchError(() => {
              this.isLoading = false;
              return of([]);
            }),
            tap(() => (this.isLoading = false))
          )
          .subscribe((results) => {
            this.filteredOptions = results;
            this.isDropdownOpen = this.filteredOptions.length > 0;
            this._cdr.markForCheck();
          });
      } else {
        this.filteredOptions = [...this.options];
        this.isDropdownOpen = this.filteredOptions.length > 0;
      }
    } else if (!this.initializing) {
      this.isDropdownOpen = this.filteredOptions.length > 0;
    }
  }

  selectItem(item: any) {
    if (this.disabled) return;

    this.selectedItem = item;
    const displayValue = this.getTranslatedDisplayValue(item);
    this.searchControl.setValue(displayValue, { emitEvent: false });
    this.inputModified = false;
    this.updateValidity();
    this.onChange(item[this.valueField]);
    this.isDropdownOpen = false;
    this.markAsTouched();
  }

  handleClickOutside() {
    if (this.disabled) return;

    if (this.isDropdownOpen) {
      this.isDropdownOpen = false;
      this.markAsTouched();

      if (this.selectedItem) {
        const displayValue = this.getTranslatedDisplayValue(this.selectedItem);
        if (this.searchControl.value !== displayValue) {
          this.searchControl.setValue(displayValue, { emitEvent: false });
        }
      }
    }
  }

  private getTranslatedDisplayValue(item: any): string {
    if (!item) return '';

    const rawValue = item[this.displayField];
    if (!rawValue) return '';

    return this.translateValue ? this.translateService.instant(rawValue) : rawValue;
  }

  private filterLocalOptions(term: string): any[] {
    if (!term) {
      return this.options;
    }

    const searchTerm = term.toLowerCase();
    return this.options.filter((option) =>
      this.filterFields.some((field) => option[field]?.toLowerCase().includes(searchTerm))
    );
  }

  clearSelection() {
    if (this.disabled) return;

    this.selectedItem = null;
    this.inputModified = false;
    this.updateValidity();
    this.searchControl.setValue('', { emitEvent: false });
    this.onChange(undefined);
    this.markAsTouched();
  }

  writeValue(value: any): void {
    if (value === null || value === undefined) {
      this.selectedItem = null;
      this.inputModified = false;
      this.updateValidity();
      this.searchControl.setValue('', { emitEvent: false });
      this.onChange(undefined);
      return;
    }

    if (this.options.length > 0) {
      const foundOption = this.options.find((option) => option[this.valueField] === value);
      if (foundOption) {
        this.selectedItem = foundOption;
        const displayValue = this.getTranslatedDisplayValue(foundOption);
        this.inputModified = false;
        this.updateValidity();
        this.searchControl.setValue(displayValue, { emitEvent: false });
      }
    } else if (this.fetchFn && value) {
      this.selectedItem = { [this.valueField]: value };
      this.inputModified = false;
      this.updateValidity();
      this.fetchItemById(value);
    }

    this.isDropdownOpen = false;
  }

  private updateValidity() {
    let valid = false;
    if (this.selectedItem && !this.inputModified) {
      if (this.fetchFn) {
        valid = true;
      } else {
        valid = this.options.some((option) => option[this.valueField] === this.selectedItem[this.valueField]);
      }
    }
    if (this.options && this.options.length === 0 && !this.fetchFn) {
      valid = false;
    }
    this.isValid = valid;
    if (valid) {
      this.searchControl.setErrors(null);
    } else {
      this.searchControl.setErrors({ invalidSelection: true });
    }
  }

  private fetchItemById(id: any): void {
    if (!this.fetchFn) return;

    this.isLoading = true;
    this.fetchFn('')
      .pipe(
        catchError(() => {
          this.isLoading = false;
          return of([]);
        }),
        tap(() => (this.isLoading = false))
      )
      .subscribe((items) => {
        const foundItem = items.find((item) => item[this.valueField] === id);
        if (foundItem) {
          this.selectedItem = foundItem;
          const displayValue = this.getTranslatedDisplayValue(foundItem);
          this.searchControl.setValue(displayValue, { emitEvent: false });
        }
        this._cdr.markForCheck();
      });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  markAsTouched() {
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
    }
  }

  onInputKeydown(event: KeyboardEvent) {
    if (this.disabled || this.inputLocked()) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showInvalid(): boolean {
    return this.touched && !this.isValid;
  }

  inputLocked(): boolean {
    return !!this.selectedItem;
  }
}
